import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
} from "@mui/material";

import {
  RadioButtonUnchecked,
  CheckCircle,
  Clear as ClearIcon,
} from "@mui/icons-material";

import { useSnackbar } from "notistack";

import { backendGet, backendPut, backendPost } from "services/dataService.js";
import ClaimFlowChart from "components/ClaimFlowChart.jsx";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext.jsx";
import { PageHeader, Card } from "../../theme/components/index.js";

import DataTable from "../../components/DataTable.jsx";
import ClaimStatusBadge from "../../components/ClaimStatusBadge.jsx";

const ClaimStatusReminderAdministrationPage = () => {
  const { setBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Erinnerungsverwaltung", path: "/admin/reminders" },
    ]);
  }, [setBreadcrumbs]);

  const navigate = useNavigate();
  const location = useLocation();

  const [statusCounts, setStatusCounts] = useState({});
  const [selectedStatus, setSelectedStatus] = useState(
    new URLSearchParams(location.search).get("status") || ""
  );

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchStatusCounts = async () => {
      try {
        const response = await backendPost("data/reminders/counts-by-value", {
          label: "Anzahl der Erinnerungen",
          toSend: {
            key: "status",
          },
          setIsLoading,
        });
        if (response) {
          const counts = response.reduce((acc, item) => {
            acc[item.value] = item.count;
            return acc;
          }, {});

          setStatusCounts(counts);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der Status Counts:", error);
      }
    };

    fetchStatusCounts();
  }, []);

  const handleStatusClick = (status) => {
    // console.log(status);
    setSelectedStatus(status);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("status", status);
    navigate(`?${queryParams.toString()}`);
  };

  const statusDescriptions = {
    saved: "Gespeichert",
    pre_check: "Vorprüfung",
    correction_customer: "Korrektur Kunde",
    processing: "Der Schaden wird bearbeitet",
    closed_below_threshold: "Abgeschlossen (unter Selbstbehalt)",
    closed_not_insured: "Abgeschlossen (nicht versichert)",
    processed: "Verarbeitet",
    released: "Freigegeben",
    settled: "Abgerechnet",
    partial_settled: "Teilweise abgerechnet",
    rejected: "Abgelehnt",
    correction_insurer: "Korrektur Versicherer",
    closed: "Geschlossen",
  };

  return (
    <>
      <PageHeader
        title="Erinnerungsverwaltung"
        subtitle="Verwaltung der automatischen Erinnerungen basierend auf Schadensstatus"
      />


      {statusCounts && Object.keys(statusCounts).length > 0 && (
        <Card variant="surface" sx={{ mb: 3, p: "18px 20px" }}>
          <Typography
            sx={{ fontSize: "15px", fontWeight: 700, color: "#1B2C45" }}
          >
            Schadensstatus-Workflow
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#6b7280", mb: 1 }}>
            Status anklicken, um die zugehörigen Erinnerungen zu sehen.
            Hervorgehobene Status haben eine Erinnerung.
          </Typography>
        <ClaimFlowChart
          onStatusClick={handleStatusClick}
          orientation="horizontal"
          selectedStatus={selectedStatus}
          statusCounts={statusCounts}
          statusDescriptions={{
            saved: "Gespeichert",
            pre_check: "Vorprüfung",
            correction_customer: "Korrektur Kunde",
            processing: "Der Schaden wird bearbeitet",
            closed_below_threshold: "Abgeschlossen unter Schwelle",
            closed_not_insured: "Abgeschlossen (nicht versichert)",
            processed: "Verarbeitet",
            released: "Freigegeben",
            settled: "Abgerechnet",
            partial_settled: "Teilweise abgerechnet",
            rejected: "Abgelehnt",
            correction_insurer: "Korrektur Versicherer",
            closed: "Geschlossen",
          }}
        />
        </Card>
      )}

      {selectedStatus && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <ClaimStatusBadge
            status={selectedStatus}
            label={
              statusCounts && statusCounts[selectedStatus]
                ? statusDescriptions[selectedStatus] || selectedStatus
                : selectedStatus
            }
          />
          <IconButton
            size="small"
            aria-label="Auswahl zurücksetzen"
            onClick={() => {
              setSelectedStatus("");
              const queryParams = new URLSearchParams(location.search);
              queryParams.delete("status");
              navigate(`?${queryParams.toString()}`);
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <ReminderTable selectedStatus={selectedStatus} />
    </>
  );
};

const ReminderTable = ({ selectedStatus }) => {
  const [reminders, setReminders] = useState([]);
  const [states, setStates] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("value");
  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  // fetch initial data
  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    fetchReminders();
  }, [page, rowsPerPage, order, orderBy, selectedStatus]);

  const fetchReminders = async () => {
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sort: `${orderBy}:${order}`,
      };
      if (selectedStatus) {
        params.value = selectedStatus;
      }

      const data = await backendGet("data/reminders", {
        label: "Reminder",
        options: { params },
        setIsLoading,
        enqueueSnackbar,
      });
      if (data) {
        setReminders(data.data);
        setTotal(data.total);
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Reminder:", error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await backendGet("data/schadens/states", {
        label: "Schaden States",
      });
      const data = response.data;
      if (data) {
        const stateMap = data.states.reduce((acc, state) => {
          acc[state.name] = state.label;
          return acc;
        }, {});
        setStates(stateMap);
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Schaden States:", error);
    }
  };

  const handleSave = async (reminder) => {
    try {
      const res = await backendPut(`data/reminders/${reminder.id}`, {
        label: "Reminder",
        toSend: reminder,
        enqueueSnackbar,
      });

      if (res) {
        enqueueSnackbar("Reminder erfolgreich gespeichert", {
          variant: "success",
        });
      }

      fetchReminders();
    } catch (error) {
      console.error("Fehler beim Speichern des Reminders:", error);
    }
  };

  const handleChange = (id, field, value) => {
    setReminders((prev) =>
      prev.map((reminder) =>
        reminder.id === id ? { ...reminder, [field]: value } : reminder
      )
    );
  };

  const columns = [
    {
      accessorKey: "value",
      header: "Status",
      Cell: ({ cell }) => (
        <ClaimStatusBadge
          status={cell.getValue()}
          label={states[cell.getValue()] || cell.getValue()}
        />
      ),
    },
    { accessorKey: "send_to", header: "Benutzer" },
    { accessorKey: "title", header: "Titel" },
    {
      accessorKey: "description",
      header: "Beschreibung",
      columnVisibility: false,
    },
    {
      accessorKey: "days_until_reminder",
      header: "Intervall",
      Cell: ({ cell }) => `${cell.getValue()} Tage`,
    },
    {
      accessorKey: "active",
      header: "Aktiv",
      size: 20,
      Cell: ({ cell }) =>
        cell.getValue() ? <CheckCircle /> : <RadioButtonUnchecked />,
    },
  ];

  const activeCount = reminders.filter((r) => r.active).length;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Typography
          sx={{ fontSize: "15px", fontWeight: 700, color: "#1B2C45" }}
        >
          Erinnerungen
        </Typography>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            px: "10px",
            py: "3px",
            borderRadius: "999px",
            fontSize: "12px",
            fontWeight: 600,
            backgroundColor: "#dcfce7",
            color: "#16a34a",
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: "#16a34a",
            }}
          />
          {activeCount} aktiv
        </Box>
        {total ? (
          <Typography sx={{ fontSize: "12.5px", color: "#9ca3af" }}>
            von {total}
          </Typography>
        ) : null}
      </Box>
      <DataTable
        columns={columns}
        data={reminders}
        pageIndex={page}
        pageSize={rowsPerPage}
        totalRows={total}
        isLoading={isLoading}
        hasLoaded={true}
        enableSorting={false}
        enableColumnFilters={false}
        enableTopToolbar={false}
        enablePagination={false}
        renderDetailPanel={({ row }) => (
          <ReminderTabs
            reminder={row.original}
            onChange={(field, value) =>
              handleChange(row.original.id, field, value)
            }
            onSave={() => handleSave(row.original)}
            states={states}
          />
        )}
      />
    </>
  );
};

const ReminderTabs = ({ reminder, onChange, onSave, states }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box bgcolor="white">
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Einstellungen" />
        <Tab label="Vorschau" />
      </Tabs>
      <Box padding={2}>
        {activeTab === 0 && (
          <ReminderForm
            reminder={reminder}
            onChange={onChange}
            onSave={onSave}
            states={states}
          />
        )}
        {activeTab === 1 && <ReminderPreview reminder={reminder} />}
      </Box>
    </Box>
  );
};

const ReminderForm = ({ reminder, onChange, onSave, states = [] }) => {
  const [reminds, setReminds] = useState([]);
  const selectedRemind =
    reminds.find((r) => r.class_name === reminder.class_name) || {};

  useEffect(() => {
    const fetchReminds = async () => {
      try {
        const response = await backendPost("data/reminders/reminds", {
          label: "Reminds",
        });
        if (response?.data) {
          setReminds(response.data);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der Reminds:", error);
      }
    };

    fetchReminds();
  }, []);

  const handleButtonClick = (key) => {
    const newBody = `${reminder.body} {{${key}}}`;
    onChange("body", newBody);
  };

  return (
    <Box margin={2}>
      <Grid container spacing={2}>
        {/* Linke Spalte */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Einstellungen
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={reminder.active}
                onChange={(e) => onChange("active", e.target.checked)}
                color="primary"
              />
            }
            label="Aktiv"
          />

          <TextField
            label="Titel"
            fullWidth
            margin="normal"
            value={reminder.title}
            onChange={(e) => onChange("title", e.target.value)}
          />
          <TextField
            label="Beschreibung"
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={reminder.description}
            onChange={(e) => onChange("description", e.target.value)}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={reminder.value || ""}
              onChange={(e) => onChange("value", e.target.value)}
            >
              {Object.entries(states).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  <ClaimStatusBadge status={key} label={label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Intervall (Tage bis zur Erinnerung)"
            fullWidth
            margin="normal"
            type="number"
            value={reminder.days_until_reminder}
            onChange={(e) => onChange("days_until_reminder", e.target.value)}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Wird versendet an</InputLabel>
            <Select
              value={reminder.send_to || ""}
              onChange={(e) => onChange("send_to", e.target.value)}
            >
              <MenuItem value="kunde">Kunde</MenuItem>
              <MenuItem value="mitarbeiter">Mitarbeiter</MenuItem>
              <MenuItem value="versicherer">Versicherer</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Erinnerungstype</InputLabel>
            <Select
              value={reminder.class_name || ""}
              onChange={(e) => onChange("class_name", e.target.value)}
            >
              {reminds.map((remind) => (
                <MenuItem key={remind.class_name} value={remind.class_name}>
                  {remind.name || remind.class_name}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="textSecondary" marginTop={1}>
              {reminder.class_name
                ? reminds.find((r) => r.class_name === reminder.class_name)
                    ?.description || "Keine Beschreibung verfügbar"
                : "Bitte wählen Sie eine Klasse aus."}
            </Typography>
          </FormControl>
        </Grid>

        {/* Rechte Spalte */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Nachricht
          </Typography>

          <TextField
            label="Betreff"
            fullWidth
            margin="normal"
            value={reminder.subject}
            onChange={(e) => onChange("subject", e.target.value)}
          />
          <TextField
            label="Inhalt"
            fullWidth
            margin="normal"
            multiline
            rows={6}
            value={reminder.body}
            onChange={(e) => onChange("body", e.target.value)}
          />

          <Box>
            {selectedRemind.input_schema &&
              Object.entries(selectedRemind.input_schema).map(
                ([key, value]) => (
                  <Button
                    key={key}
                    size="small"
                    sx={{
                      backgroundColor: "gray",
                      color: "white",
                      marginRight: 2,
                      marginBottom: 1,
                      "&:hover": {
                        backgroundColor: "darkgray",
                      },
                    }}
                    onClick={() => handleButtonClick(key)}
                  >
                    {value.label}
                  </Button>
                )
              )}
          </Box>
        </Grid>
      </Grid>

      <Box marginTop={2} textAlign="right">
        <Button variant="contained" color="primary" onClick={onSave}>
          Speichern
        </Button>
      </Box>
    </Box>
  );
};

const ReminderPreview = ({ reminder }) => {
  const [inputValues, setInputValues] = useState({});
  const [previewValues, setPreviewValues] = useState({ subject: "", body: "" });
  const [debouncedValues, setDebouncedValues] = useState({});

  const input_schema =
    typeof reminder?.input_schema === "string"
      ? JSON.parse(reminder.input_schema)
      : reminder?.input_schema;

  useEffect(() => {
    if (reminder?.input_schema) {
      const initialValues = Object.keys(input_schema).reduce(
        (acc, key) => ({
          ...acc,
          [key]: input_schema[key].default || input_schema[key].example || "",
        }),
        {}
      );
      setInputValues(initialValues);
    }
  }, [reminder]);

  // Debounce-Effekt
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValues(inputValues);
    }, 300); // 300ms Verzögerung

    return () => clearTimeout(handler); // Cleanup
  }, [inputValues]);

  useEffect(() => {
    if (Object.keys(debouncedValues).length > 0) {
      fetchPreview();
    }
  }, [debouncedValues]);

  const handleInputChange = (field, value) => {
    setInputValues((prev) => ({ ...prev, [field]: value }));
  };

  const fetchPreview = useCallback(async () => {
    try {
      const response = await backendPost("data/reminders/preview", {
        label: "Preview",
        toSend: {
          reminder_id: reminder.id,
          data: debouncedValues,
        },
      });
      if (response) {
        setPreviewValues(response);
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Vorschau:", error);
    }
  }, [debouncedValues, reminder?.id]);

  return (
    <Box padding={2}>
      <Grid container spacing={2}>
        <Grid item size={{ xs: 6 }}>
          <Typography variant="h6" gutterBottom>
            Standard/Beispielwerte
          </Typography>
          {input_schema &&
            Object.keys(input_schema).map((key) => (
              <TextField
                key={key}
                label={key}
                fullWidth
                margin="normal"
                value={inputValues[key] || ""}
                onChange={(e) => handleInputChange(key, e.target.value)}
              />
            ))}
        </Grid>
        <Grid item size={{ xs: 6 }}>
          <Typography variant="h6" gutterBottom>
            Vorschau
          </Typography>
          <Box>
            <Typography variant="subtitle1">Betreff:</Typography>
            <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
              {previewValues.subject}
            </Typography>
          </Box>
          <Box marginTop={2}>
            <Typography variant="subtitle1">Inhalt:</Typography>
            <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
              {previewValues.body}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClaimStatusReminderAdministrationPage;
